import { isNativePlatform } from "@/lib/capacitor/platform";
import type { WebVersionFeature } from "@/components/mobile/WebVersionModal";

/**
 * Open the web checkout/billing surface in the SYSTEM browser (real Safari).
 *
 * LEGAL BASIS (US storefront): after the May 2025 contempt order in Epic v.
 * Apple — upheld on appeal, SCOTUS stay denied — US apps may link out to
 * external purchase flows with no entitlement and no commission. Spotify,
 * Kindle and Patreon shipped this pattern immediately. Our agents are
 * US-based, so the app may now SAY "upgrade on insuracentral.com" and take
 * you there.
 *
 * TWO RULES THIS FILE ENFORCES:
 *  1. The payment flow NEVER runs inside our WKWebView — AppLauncher hands the
 *     URL to the real Safari app. An embedded checkout is still the fastest
 *     way to lose a review.
 *  2. Still ZERO IAP anywhere. Mixing IAP with external links invites the
 *     scrutiny this ruling freed us from.
 *
 * ⚠️ US-storefront only. If we ever ship to other storefronts, this must gate
 * by storefront region — the ruling does not apply elsewhere.
 */
const WEB_BASE = "https://app.insuracentral.com";

/** Everything lands on the app home — the user asked for one simple door.
 *  (Per-feature deep links can come back later by editing this map.) */
export const CHECKOUT_URLS: Record<WebVersionFeature, string> = {
  subscription: WEB_BASE,
  "area-code": WEB_BASE,
  payment: WEB_BASE,
  billing: WEB_BASE,
  // Signup deep-links to the actual signup page — landing a brand-new user on
  // the app home (a login screen) was a dead end.
  signup: `${WEB_BASE}/auth/sign-up`,
};

export async function openExternalCheckout(feature: WebVersionFeature): Promise<void> {
  const url = CHECKOUT_URLS[feature] ?? WEB_BASE;
  if (isNativePlatform()) {
    try {
      const { AppLauncher } = await import("@capacitor/app-launcher");
      await AppLauncher.openUrl({ url });
      return;
    } catch {
      /* plugin missing (old build) — fall through to window.open */
    }
  }
  window.open(url, "_blank", "noopener");
}
